/**
 * 익명 세션 관리 유틸리티
 * 비로그인 유저의 문제 풀이 이력을 추적하기 위한 세션 ID 관리
 */

// localStorage 키 상수
const ANONYMOUS_SESSION_KEY = 'anonymous_session_id';
const PROBLEM_SOLVED_COUNT_KEY = 'problem_solved_count';
const HIDE_HISTORY_KEY = 'should_hide_history';
const GA_5_PROBLEMS_SENT_KEY = 'ga_5_problems_conversion_sent';

/**
 * localStorage 접근 가능 여부 확인
 * 일부 브라우저 환경(프라이빗 브라우징, WebView 등)에서는
 * window는 존재하지만 localStorage가 null이거나 접근 시 에러가 발생할 수 있음
 */
function isLocalStorageAvailable(): boolean {
  try {
    return typeof window !== 'undefined' && window.localStorage !== null;
  } catch {
    return false;
  }
}

/**
 * UUID v4 생성 함수
 */
function generateUUID(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
}

/**
 * 익명 세션 ID를 가져오거나 새로 생성
 * @returns 익명 세션 ID
 */
export function getAnonymousSessionId(): string {
  if (!isLocalStorageAvailable()) {
    return '';
  }

  let sessionId = localStorage.getItem(ANONYMOUS_SESSION_KEY);

  if (!sessionId) {
    sessionId = generateUUID();
    localStorage.setItem(ANONYMOUS_SESSION_KEY, sessionId);
  }

  return sessionId;
}

/**
 * 문제 풀이 횟수 증가
 * @returns 현재 풀이 횟수
 */
export function incrementProblemSolvedCount(): number {
  if (!isLocalStorageAvailable()) {
    return 0;
  }

  const currentCount = getProblemSolvedCount();
  const newCount = currentCount + 1;
  localStorage.setItem(PROBLEM_SOLVED_COUNT_KEY, newCount.toString());

  return newCount;
}

/**
 * 현재 문제 풀이 횟수 조회
 * @returns 현재 풀이 횟수
 */
export function getProblemSolvedCount(): number {
  if (!isLocalStorageAvailable()) {
    return 0;
  }

  const count = localStorage.getItem(PROBLEM_SOLVED_COUNT_KEY);
  return count ? parseInt(count, 10) : 0;
}

/**
 * 12개 문제를 풀었는지 확인
 * @returns 12개 이상 풀었으면 true
 */
export function hasSolvedTwelveProblems(): boolean {
  return getProblemSolvedCount() >= 12;
}

/**
 * 이력 숨김 상태 설정
 * @param hide 숨길지 여부
 */
export function setHideHistory(hide: boolean): void {
  if (!isLocalStorageAvailable()) {
    return;
  }

  if (hide) {
    localStorage.setItem(HIDE_HISTORY_KEY, 'true');
  } else {
    localStorage.removeItem(HIDE_HISTORY_KEY);
  }
}

/**
 * 이력 숨김 상태 조회
 * 기본값은 true (비회원은 기본적으로 이력이 숨겨짐)
 * @returns 이력이 숨겨져 있으면 true
 */
export function shouldHideHistory(): boolean {
  if (!isLocalStorageAvailable()) {
    return true;
  }

  // 기본값 true: 명시적으로 'false'가 저장되어 있지 않으면 숨김
  return localStorage.getItem(HIDE_HISTORY_KEY) !== 'false';
}

/**
 * 익명 세션 데이터 초기화 (회원가입 후)
 */
export function clearAnonymousSession(): void {
  if (!isLocalStorageAvailable()) {
    return;
  }

  localStorage.removeItem(ANONYMOUS_SESSION_KEY);
  localStorage.removeItem(PROBLEM_SOLVED_COUNT_KEY);
  localStorage.removeItem(HIDE_HISTORY_KEY);
  localStorage.removeItem(GA_5_PROBLEMS_SENT_KEY);
}

/**
 * 익명 세션 ID가 존재하는지 확인
 * @returns 세션 ID가 있으면 true
 */
export function hasAnonymousSession(): boolean {
  if (!isLocalStorageAvailable()) {
    return false;
  }

  return !!localStorage.getItem(ANONYMOUS_SESSION_KEY);
}

/**
 * GA 5개 문제 풀이 전환 이벤트가 이미 전송되었는지 확인
 * @returns 이미 전송되었으면 true
 */
export function hasGA5ProblemsConversionSent(): boolean {
  if (!isLocalStorageAvailable()) {
    return true; // SSR 또는 localStorage 접근 불가 시 전송 방지
  }

  return localStorage.getItem(GA_5_PROBLEMS_SENT_KEY) === 'true';
}

/**
 * GA 5개 문제 풀이 전환 이벤트 전송 완료 표시
 */
export function markGA5ProblemsConversionSent(): void {
  if (!isLocalStorageAvailable()) {
    return;
  }

  localStorage.setItem(GA_5_PROBLEMS_SENT_KEY, 'true');
}
